import { parseNrcReportDate } from './nrcReactorStatus';

export const NRC_NATIONAL_FEED_URL = 'https://www.nrc.gov/reading-rm/doc-collections/event-status/reactor-status/PowerReactorStatusForLast365Days.txt';
export const NRC_FEED_MAX_BYTES = 4 * 1024 * 1024;
export const NRC_FEED_TTL_MS = 6 * 60 * 60_000;
export interface NrcFeedCounts { sourceRows: number; sourceUnits: number; sourceDates: number; earliestDate: string; latestDate: string }
export interface NrcFeedReceipt extends NrcFeedCounts {
  schemaVersion: 1; datasetId: 'nrc-reactor-status'; sourceUrl: typeof NRC_NATIONAL_FEED_URL;
  retrievedAt: string; ttlMs: typeof NRC_FEED_TTL_MS; allReturnedRowsAccounted: true;
  object: { path: string; generation: string; bytes: number; sha256: string };
  http: { contentType: string | null; lastModified: string | null; etag: string | null };
}
export interface NrcNationalFeed { text: string; provenance: NrcFeedReceipt & { cacheStatus: 'gcs-hit' | 'refreshed' } }

/** Account every row in the national rolling-year file before any cache admission.
 * Daily fleet omissions remain visible to the separate per-site status parser. */
export function validateNrcNationalFeed(text: string, now = Date.now()): NrcFeedCounts {
  const bytes = new TextEncoder().encode(text).byteLength;
  if (bytes === 0 || bytes > NRC_FEED_MAX_BYTES) throw new Error('NRC national feed is empty or oversized');
  // The official complete feed has no trailing newline; validate the final
  // record just like every preceding row instead of inventing a delimiter.
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/);
  if (lines.shift() !== 'ReportDt|Unit|Power') throw new Error('NRC national feed header differs');
  const seen = new Set<string>(), units = new Set<string>(), dates = new Set<string>();
  const today = new Date(now).toISOString().slice(0, 10);
  for (const line of lines) {
    if (!line.trim()) continue;
    const columns = line.split('|');
    const date = columns.length === 3 ? parseNrcReportDate(columns[0]!) : null;
    const unit = columns[1]?.trim(), rawPower = columns[2]?.trim();
    if (!date || date > today || !unit || unit.length > 128 || /[\x00-\x1f]/.test(unit)
      || !/^\d+(?:\.\d+)?$/.test(rawPower ?? '') || !Number.isFinite(Number(rawPower)) || Number(rawPower) > 100) throw new Error('NRC national feed contains an invalid source row');
    const key = `${date}|${unit}`;
    if (seen.has(key)) throw new Error('NRC national feed repeats a unit and report date');
    seen.add(key); units.add(unit); dates.add(date);
  }
  const ordered = [...dates].sort();
  const earliestDate = ordered[0], latestDate = ordered.at(-1);
  // A rolling annual national file must contain substantial annual/fleet coverage.
  // This is a truncation sanity bound, not an inferred missing-day or fleet count.
  if (!earliestDate || !latestDate || dates.size < 300 || dates.size > 367 || units.size < 50 || units.size > 250 || seen.size < 15_000
    || (Date.parse(latestDate) - Date.parse(earliestDate)) / 86_400_000 < 300) throw new Error('NRC rolling-year national coverage is incomplete');
  return { sourceRows: seen.size, sourceUnits: units.size, sourceDates: dates.size, earliestDate, latestDate };
}
export function isNrcFeedReceipt(value: unknown): value is NrcFeedReceipt {
  if (!value || typeof value !== 'object') return false;
  const r = value as NrcFeedReceipt, o = r.object;
  return r.schemaVersion === 1 && r.datasetId === 'nrc-reactor-status' && r.sourceUrl === NRC_NATIONAL_FEED_URL
    && r.ttlMs === NRC_FEED_TTL_MS && r.allReturnedRowsAccounted === true && typeof r.retrievedAt === 'string' && Number.isFinite(Date.parse(r.retrievedAt))
    && !!o && typeof o.path === 'string' && /^refdata\/nrc-reactor-status\/[a-zA-Z0-9_-]+\/raw\/[a-f0-9]{64}\.txt$/.test(o.path)
    && o.path.endsWith(`/${o.sha256}.txt`) && typeof o.sha256 === 'string' && /^[a-f0-9]{64}$/.test(o.sha256) && typeof o.generation === 'string' && /^[1-9]\d*$/.test(o.generation)
    && Number.isSafeInteger(o.bytes) && o.bytes > 0 && o.bytes <= NRC_FEED_MAX_BYTES
    && Number.isSafeInteger(r.sourceRows) && r.sourceRows >= 15_000 && Number.isSafeInteger(r.sourceUnits) && r.sourceUnits >= 50 && r.sourceUnits <= 250
    && Number.isSafeInteger(r.sourceDates) && r.sourceDates >= 300 && r.sourceDates <= 367
    && /^\d{4}-\d{2}-\d{2}$/.test(r.earliestDate) && /^\d{4}-\d{2}-\d{2}$/.test(r.latestDate)
    && !!r.http && [r.http.contentType, r.http.lastModified, r.http.etag].every(x => x === null || (typeof x === 'string' && x.length <= 500));
}
export function nrcReceiptIsFresh(receipt: NrcFeedReceipt, now = Date.now()): boolean {
  const age = now - Date.parse(receipt.retrievedAt);
  return age >= -5 * 60_000 && age < NRC_FEED_TTL_MS;
}
